function stepDetail(step, index, currentIndex, allComplete) {
  if (!allComplete && index === currentIndex) return 'You are here';
  if (step.detail) return step.detail;
  if (step.complete || allComplete) return '—';
  if (index === currentIndex + 1) return 'Awaiting';
  return '—';
}

function StepDot({ complete, current }) {
  if (complete) {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-eorig-navy-600" aria-hidden />;
  }
  if (current) {
    return (
      <span
        className="h-2 w-2 shrink-0 rounded-full border border-[#FFE8A3] bg-[#987200]"
        aria-hidden
      />
    );
  }
  return <span className="h-2 w-2 shrink-0 rounded-full bg-[#8D8D8D]" aria-hidden />;
}

/**
 * Figma packaging-lifecycle card (UMB eVault 282:48111).
 * `steps` must include `{ label, complete }` and may include `{ detail, id }`.
 */
export function LifecycleStepBar({ steps, title = 'Packaging lifecycle' }) {
  const currentIndex = steps.findIndex((step) => !step.complete);
  const allComplete = currentIndex === -1;
  const activeIndex = allComplete ? Math.max(steps.length - 1, 0) : currentIndex;

  if (!steps.length) return null;

  return (
    <section
      aria-label={title}
      className="overflow-x-auto rounded-lg border border-[#E6EAF0] bg-white px-[17px] pb-4 pt-[17px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <ol className="flex w-full items-center">
        {steps.map((step, index) => {
          const isCurrent = !allComplete && index === currentIndex;
          const isLast = index === steps.length - 1;
          const upcoming = !step.complete && !isCurrent;
          const detail = stepDetail(step, index, currentIndex, allComplete);
          const titleColor = upcoming ? 'text-[#5A5A5A]' : 'text-[#1F2933]';
          const detailColor = isCurrent ? 'text-[#7A5C00]' : 'text-[#5A5A5A]';

          return (
            <li
              key={step.id || step.label}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={step.label}
              className={`flex items-center gap-4 ${isLast ? 'shrink-0' : 'flex-1 pr-3'}`}
            >
              <div className="flex h-[61px] shrink-0 flex-col justify-between">
                <p className="whitespace-nowrap text-[12px] font-semibold uppercase leading-4 text-[#5A5A5A]">
                  STEP {index + 1}
                </p>
                <div className="flex items-center gap-2">
                  <StepDot complete={step.complete || allComplete} current={isCurrent} />
                  <p
                    className={`whitespace-nowrap text-[14px] font-semibold leading-[21px] ${titleColor}`}
                  >
                    {step.label}
                  </p>
                </div>
                <p className={`whitespace-nowrap text-[12px] leading-4 ${detailColor}`}>
                  {detail}
                </p>
              </div>
              {isLast ? null : (
                <span
                  className="h-[2px] min-w-[40px] flex-1"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(90deg, #D1D5DB 0, #D1D5DB 4px, transparent 4px, transparent 8px)',
                  }}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
      <span className="sr-only">
        {allComplete
          ? 'Complete'
          : `Step ${activeIndex + 1} of ${steps.length} · ${steps[activeIndex]?.label}`}
      </span>
    </section>
  );
}
